import type {
  ActivityStepProgress,
  ActivityStepObservation,
  Assessment,
  AssessmentEntryMode,
  AssessmentEntry,
  AssessmentScore,
  AssessmentStatus
} from "@/types/assessment";
import { apiGet, apiPatch, apiPost, apiPut } from "@/services/api/client";

export async function fetchAssessments(): Promise<Assessment[]> {
  return apiGet<Assessment[]>("/assessments");
}

export async function fetchAssessmentById(assessmentId: string): Promise<Assessment | undefined> {
  return apiGet<Assessment>(`/assessments/${encodeURIComponent(assessmentId)}`);
}

export async function fetchAssessmentEntries(assessmentId: string): Promise<AssessmentEntry[]> {
  return apiGet<AssessmentEntry[]>(
    `/assessments/${encodeURIComponent(assessmentId)}/entries`,
  );
}

export interface ReportAggregate {
  assessedCount: number;
  score: number;
  maxScore: number;
  average: number;
  scorePercent: number;
}

export interface ReportSummary {
  schemaVersion: "report-summary-v1";
  calculatorVersion: string;
  catalogVersion: number;
  coverageSignature: string;
  total: ReportAggregate;
  domains: Array<
    ReportAggregate & {
      code: string;
      name: string;
    }
  >;
  subdomains: Array<
    ReportAggregate & {
      key: string;
      domainCode: string;
      name: string;
    }
  >;
}

export interface AssessmentReport {
  assessment: Assessment;
  summary: ReportSummary;
  observations: Array<{
    entryCode: string;
    comment: string;
  }>;
  activityObservations?: Array<{
    activityCode: string;
    stepId: string;
    text: string;
    version: number;
    updatedByDisplayName?: string;
    updatedAt: string;
  }>;
}

export async function fetchAssessmentReport(assessmentId: string): Promise<AssessmentReport> {
  return apiGet<AssessmentReport>(`/assessments/${encodeURIComponent(assessmentId)}/report`);
}

export async function compareAssessmentReports(
  assessmentIds: string[],
): Promise<AssessmentReport[]> {
  return apiPost<AssessmentReport[]>("/reports/compare", { assessmentIds });
}

export async function startOrResumeAssessment(
  studentId: string,
  type: AssessmentEntryMode,
): Promise<Assessment> {
  return apiPost<Assessment>("/assessments/find-or-create", {
    studentId,
    type
  });
}

export async function updateAssessmentEntryMode(
  assessmentId: string,
  entryMode: AssessmentEntryMode,
): Promise<Assessment> {
  return apiPatch<Assessment>(
    `/assessments/${encodeURIComponent(assessmentId)}/entry-mode`,
    { entryMode },
  );
}

export interface SaveAssessmentEntryPayload {
  assessmentId: string;
  entryCode: string;
  score?: AssessmentScore | null;
  comment?: string;
  expectedVersion: number;
}

export async function saveAssessmentEntry(
  payload: SaveAssessmentEntryPayload,
): Promise<AssessmentEntry> {
  return apiPut<AssessmentEntry>(
    `/assessments/${encodeURIComponent(payload.assessmentId)}/entries/${encodeURIComponent(payload.entryCode)}`,
    {
      score: payload.score,
      comment: payload.comment ?? "",
      expectedVersion: payload.expectedVersion
    },
  );
}

export async function updateAssessmentStatus(
  assessmentId: string,
  status: AssessmentStatus,
): Promise<Assessment | undefined> {
  if (status === "in_progress") {
    throw new Error("已提交评估必须通过修订接口更正");
  }
  return apiPost<Assessment>(`/assessments/${encodeURIComponent(assessmentId)}/submit`);
}

export async function fetchActivityStepProgress(
  assessmentId: string,
): Promise<ActivityStepProgress[]> {
  return apiGet<ActivityStepProgress[]>(
    `/assessments/${encodeURIComponent(assessmentId)}/activity-steps`,
  );
}

export async function fetchActivityObservations(
  assessmentId: string,
): Promise<ActivityStepObservation[]> {
  return apiGet<ActivityStepObservation[]>(
    `/assessments/${encodeURIComponent(assessmentId)}/activity-observations`,
  );
}

export interface SaveActivityObservationPayload {
  assessmentId: string;
  activityCode: string;
  stepId: string;
  text: string;
  expectedVersion: number;
}

export async function saveActivityObservation(
  payload: SaveActivityObservationPayload,
): Promise<ActivityStepObservation> {
  return apiPut<ActivityStepObservation>(
    `/assessments/${encodeURIComponent(payload.assessmentId)}/activity-observations/${encodeURIComponent(payload.activityCode)}/${encodeURIComponent(payload.stepId)}`,
    {
      text: payload.text,
      expectedVersion: payload.expectedVersion
    },
  );
}

export interface UpdateActivityStepPayload {
  assessmentId: string;
  activityCode: string;
  stepId: string;
  status: ActivityStepProgress["status"];
  expectedVersion: number;
}

export async function updateActivityStepStatus(
  payload: UpdateActivityStepPayload,
): Promise<ActivityStepProgress> {
  return apiPut<ActivityStepProgress>(
    `/assessments/${encodeURIComponent(payload.assessmentId)}/activity-steps/${encodeURIComponent(payload.activityCode)}/${encodeURIComponent(payload.stepId)}`,
    {
      status: payload.status === "skipped" ? "pending" : payload.status,
      expectedVersion: payload.expectedVersion
    },
  );
}

export interface IepGoalPlan {
  goalEntryCodes: string[];
  evaluationNote: string;
}

export interface IepContent {
  analysis: Record<string, string>;
  development: Record<string, string>;
  summary: Record<string, string>;
  educationPlan: Record<string, IepGoalPlan>;
  supportServices: Array<{
    serviceName: string;
    content: string;
    provider: string;
    frequency: string;
    startDate: string;
    endDate: string;
    note: string;
  }>;
  familyGuidance: { guidance: string; parentSupport: string };
}

export interface IepDraft {
  assessmentId: string;
  studentId: string;
  contentSchemaVersion: string;
  content: IepContent;
  version: number;
  baseConfirmedVersion: number;
  sourceGenerationAttemptId?: string;
  createdByDisplayName?: string;
  updatedByDisplayName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IepVersionSummary {
  version: number;
  confirmedAt: string;
  confirmedByDisplayName?: string;
  sourceGenerationAttemptId?: string;
  previousVersionId?: string;
}

export interface ConfirmedIep {
  id: string;
  assessmentId: string;
  contentSchemaVersion: string;
  content: IepContent;
  version: number;
  confirmedBy: string;
  confirmedAt: string;
  sourceGenerationAttemptId?: string;
}

export async function fetchCapabilities(): Promise<{ iepAiEnabled: boolean }> {
  return apiGet("/system/capabilities");
}

export async function generateIep(
  assessmentId: string,
  requestId: string,
  expectedDraftVersion: number,
): Promise<{
  attemptId: string;
  content: IepContent;
  draftVersion: number;
  applied: boolean;
  replayed: boolean;
}> {
  return apiPost(`/assessments/${encodeURIComponent(assessmentId)}/iep/generate`, {
    requestId,
    expectedDraftVersion
  });
}

export async function fetchIepDraft(
  assessmentId: string,
): Promise<{ draft: IepDraft | null }> {
  return apiGet(`/assessments/${encodeURIComponent(assessmentId)}/iep/draft`);
}

export async function saveIepDraft(
  assessmentId: string,
  content: IepContent,
  expectedVersion: number,
  sourceAttemptId?: string,
): Promise<IepDraft> {
  return apiPut(`/assessments/${encodeURIComponent(assessmentId)}/iep/draft`, {
    content,
    expectedVersion,
    sourceAttemptId
  });
}

export async function fetchIepVersions(
  assessmentId: string,
): Promise<IepVersionSummary[]> {
  return apiGet(`/assessments/${encodeURIComponent(assessmentId)}/iep/versions`);
}

export async function fetchConfirmedIep(assessmentId: string): Promise<ConfirmedIep> {
  return apiGet(`/assessments/${encodeURIComponent(assessmentId)}/iep`);
}

export async function confirmIep(
  assessmentId: string,
  content: IepContent,
  expectedVersion: number,
  expectedDraftVersion?: number,
  sourceAttemptId?: string,
): Promise<ConfirmedIep> {
  return apiPut(`/assessments/${encodeURIComponent(assessmentId)}/iep`, {
    content,
    expectedVersion,
    expectedDraftVersion,
    confirmed: true,
    sourceAttemptId
  });
}

export interface DashboardSummary {
  inProgressAssessments: number;
  completedAssessments: number;
  pendingIepDrafts: number;
  reportableAssessments: number;
}

export async function fetchDashboardSummary(): Promise<DashboardSummary> {
  return apiGet<DashboardSummary>("/dashboard/summary");
}
